import { Add } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { Control, FieldArrayWithId, UseFieldArrayReturn } from 'react-hook-form';

import { Button } from '@/components/Button';
import { useYaMetrika } from '@/entities/yaMetrika';
import { AnalyticsEvent } from '@/types/yametrika';

import { DateEntryItem } from '../DateEntryItem';
import { FormValues } from '../../types';

import s from './DateEntriesSection.module.scss';

const MAX_DATE_ENTRIES = 5;

type DateEntriesSectionProps = {
  control: Control<FormValues>;
  fields: FieldArrayWithId<FormValues, 'dateEntries', 'id'>[];
  watchedDateEntries: { startDate?: string; duration?: string }[] | undefined;
  errors: any;
  isEdit?: boolean;
  isCopy?: boolean;
  remove: UseFieldArrayReturn<FormValues, 'dateEntries'>['remove'];
  append: UseFieldArrayReturn<FormValues, 'dateEntries'>['append'];
  setValue: (name: any, value: any) => void;
  hasPreselectedDate?: boolean;
  isSingleEntry?: boolean;
  isTaskSelected?: boolean;
};

export const DateEntriesSection: React.FC<DateEntriesSectionProps> = observer(
  ({
    control,
    fields,
    watchedDateEntries,
    errors,
    isEdit,
    isCopy,
    remove,
    append,
    setValue,
    hasPreselectedDate,
    isSingleEntry,
    isTaskSelected,
  }) => {
    const canAddMore = !isSingleEntry && fields.length < MAX_DATE_ENTRIES;
    const showAppendButton = !isEdit && !isCopy && canAddMore;
    const { sendEvent } = useYaMetrika();

    const handleAddDate = () => {
      const currentDate = DateTime.local().setLocale('ru').toISODate();

      append({
        startDate: currentDate ?? '',
        duration: '',
      });

      sendEvent(AnalyticsEvent.clickAddDate);
    };

    return (
      <>
        <div className={s.date_entries}>
          {fields.map((field, index) => {
            const entry = watchedDateEntries?.[index];
            const isFirstEntryLocked = index === 0 && (hasPreselectedDate || isSingleEntry);

            return (
              <DateEntryItem
                key={field.id}
                control={control}
                index={index}
                entry={entry}
                errors={errors}
                showDeleteButton={!isSingleEntry && fields.length > 1}
                onDelete={() => remove(index)}
                setValue={setValue}
                autoFocusDuration={
                  index === 0 &&
                  (isEdit || isCopy || ((hasPreselectedDate || isSingleEntry) && isTaskSelected))
                }
                isDateLocked={isFirstEntryLocked}
              />
            );
          })}
        </div>
        {showAppendButton && (
          <Button variant="outlined" onClick={handleAddDate} startIcon={<Add />} sx={{ mt: 1 }}>
            Добавить дату
          </Button>
        )}
      </>
    );
  },
);
